import React from 'react';

import {
  browserHistory,
  IndexRedirect,
  IndexRoute,
  Redirect,
  Route,
  Router,
} from 'react-router';

import {LoadableAccountOverviewPage} from 'app/account/components/AccountOverviewPage';
import {APISettingsPage} from 'app/account/components/APISettingsPage';
import {WebhookSettingsPage} from 'app/account/components/WebhookSettingsPage';
import {CookieAuthChecker} from 'app/auth/components/CookieAuthChecker';
import {
  CreateDocBreadcrumb,
  createStaticTextBreadcrumb,
  DocNameBreadcrumb,
  DocVersionBreadcrumb,
  EditDocBreadcrumb,
} from 'app/components/general/breadcrumbs';
import HardRefreshToServer from 'app/components/general/HardRefreshToServer';
import {HomePageRedirect} from 'app/components/general/HomePageRedirect';
import {SPBootstrapPage} from 'app/containers/general/SPBasePage';
import {DocumentFoldersBreadcrumbs} from 'app/documents/components/DocumentFoldersBreadcrumbs';
import {
  DocumentsDashboard,
  DocumentsDashboardBreadcrumb,
  TextLessDocumentsDashboardBreadcrumb,
} from 'app/documents/components/DocumentsDashboard';
import {NewDocumentPage} from 'app/documents/components/NewDocumentPage';
import {SPFrame} from 'app/general/structures/SPFrame';
import {SwitchAccountPage} from 'app/general/SwitchAccountPage';
import {WrapStaticContentPage} from 'app/general/WrapStaticContentPage';
import {KBEditPage} from 'app/kbs/components/KBEditPage';
import {
  KBsDashboardBreadcrumb,
  KnowledgebasesDashboard,
} from 'app/kbs/components/KnowledgebasesDashboard';
import {NewKnowledgebasePage} from 'app/kbs/components/NewKnowledgebasePage';
import {
  NotificationsPage,
  NotificationsPageBreadcrumb,
} from 'app/notifly/components/NotificationsPage';
import {NewPolicyPage} from 'app/policies/components/NewPolicyPage';
import {
  PoliciesDashboard,
  PoliciesDashboardBreadcrumb,
} from 'app/policies/components/PoliciesDashboard';
import PolicyDetailPage from 'app/policies/components/PolicyDetailPage';
import {LoadablePolicyEditPage} from 'app/policies/components/PolicyEditPage/LoadablePolicyEditPage';
import {NewProcedurePage} from 'app/procedures/components/NewProcedurePage';
import ProcedureDetailPage, {
  MaybeProcedureProcessContainerBreadcrumb,
} from 'app/procedures/components/ProcedureDetailPage';
import {ProcedureTaskReportPage} from 'app/procedures/components/ProcedureDetailPage/ProcedureTaskReportPage';
import {LoadableProcedureEditPage} from 'app/procedures/components/ProcedureEditPage/LoadableProcedureEditPage';
import {
  ProceduresDashboard,
  ProceduresDashboardBreadcrumb,
} from 'app/procedures/components/ProceduresDashboard';
import {LoadableProcessEditPage} from 'app/process/components/LoadableProcessEditPage';
import {ProcessCreatePage} from 'app/process/components/ProcessCreatePage';
import {
  ProcessesDashboard,
  ProcessesDashboardBreadcrumb,
} from 'app/process/components/ProcessesDashboard';
import {ProcessPage} from 'app/process/components/ProcessPage';
import {ProcessTaskReportPage} from 'app/process/components/ProcessTaskReportPage';
import {AttemptQuizPage} from 'app/quizzes/components/AttemptQuizPage/AttemptQuizPage';
import {AttemptResultPage} from 'app/quizzes/components/AttemptResultPage/AttemptResultPage';
import {NewQuizPage} from 'app/quizzes/components/NewQuizPage/NewQuizPage';
import {QuizPage} from 'app/quizzes/components/QuizDetailPage';
import {LoadableQuizEditPage} from 'app/quizzes/components/QuizEditPage/LoadableQuizEditPage';
import {
  QuizzesDashboard,
  QuizzesDashboardBreadcrumb,
} from 'app/quizzes/components/QuizzesDashboard/QuizzesDashboard';
import {RedirectToTakeQuizPage} from 'app/quizzes/components/TakeQuizPage/RedirectToTakeQuizPage';
import {RecycleBinPage} from 'app/recycle-bin/components/RecycleBinPage';
import {ReferralsPage} from 'app/referrals/components/ReferralsPage';
import {ReportsDetailPage} from 'app/reports/components/ReportsDetailPage';
import {
  ReportsBreadcrumb,
  ReportsPage,
} from 'app/reports/components/ReportsPage';
import {
  ReportsResultBreadcrumb,
  ReportsRunPage,
} from 'app/reports/components/ReportsRunPage';
import {SCIMBreadcrumb, SCIMPage} from 'app/scim/components/SCIMPage';
import {
  SearchBreadcrumb,
  SearchDashboard,
} from 'app/search/components/SearchDashboard';
import {
  TagsDashboard,
  TagsDashboardBreadcrumb,
} from 'app/tags/components/TagsDashboard';
import {CreateTaskInstancePage} from 'app/tasks/components/CreateTaskInstancePage';
import {
  InnerTaskDetailBreadcrumb,
  TaskDetailPage,
} from 'app/tasks/components/ProcedureTaskPage';
import {ProcessTaskPage} from 'app/tasks/components/ProcessTaskPage';
import {
  RedirecToLastUsedTasksDashboard,
  TasksDashboard,
  TasksPageBreadcrumb,
} from 'app/tasks/components/TasksDashboard';
import {TeamCreatePage} from 'app/teams/components/TeamCreatePage';
import {TeamDetailPage} from 'app/teams/components/TeamDetailPage';
import {
  TeamsDashboard,
  TeamsDashboardBreadcrumb,
} from 'app/teams/components/TeamsDashboard';
import {
  UserCreateBreadcrumb,
  UserCreatePage,
} from 'app/user/components/UserCreatePage';
import {UserDetailPage} from 'app/user/components/UserDetailPage';
import {ActivityList} from 'app/user/components/UserDetailPage/ActivityList';
import {InvitationList} from 'app/user/components/UserDetailPage/InvitationList';
import {PoliciesList} from 'app/user/components/UserDetailPage/PoliciesList';
import {ProcedureList} from 'app/user/components/UserDetailPage/ProcedureList';
import {ProcessList} from 'app/user/components/UserDetailPage/ProcessList';
import {TeamList} from 'app/user/components/UserDetailPage/TeamList';
import {UserSignoffsList} from 'app/user/components/UserDetailPage/UserSignoffsList';
import {
  UsersDashboard,
  UsersDashboardBreadcrumb,
} from 'app/user/components/UsersDashboard';
import {
  ForwardToPolicyEditPage,
  ForwardToProcedureEditPage,
  ForwardToProcessEditPage,
  ForwardToQuizEditPage,
} from 'app/version/components/ForwardToDocumentEditPage';
import {ImportDashboard} from 'app/welcome/components/ImportDashboard';
import {InviteDashboard} from 'app/welcome/components/InviteDashboard';
import {WelcomeDashboard} from 'app/welcome/components/WelcomeDashboard';

export const RootRouter = () => (
  <Router history={browserHistory}>
    <Route component={CookieAuthChecker}>
      <Route component={SPBootstrapPage}>
        <Route
          props={{
            breadcrumb: TextLessDocumentsDashboardBreadcrumb,
          }}
        >
          <Route
            props={{
              breadcrumb: DocNameBreadcrumb,
            }}
            path="documents/welcome/"
            component={WelcomeDashboard}
          />
          <Route
            props={{
              breadcrumb: DocNameBreadcrumb,
            }}
            path="documents/import/"
            component={ImportDashboard}
          />
          <Route
            props={{
              breadcrumb: DocNameBreadcrumb,
            }}
            path="documents/invite/"
            component={InviteDashboard}
          />
        </Route>

        <Route component={SPFrame}>
          <Route
            props={{
              breadcrumb: TextLessDocumentsDashboardBreadcrumb,
            }}
          >
            <Route path="home/" component={HomePageRedirect} />
            <Route
              path="/documents/"
              props={{
                activateNavLink: 'documents',
                breadcrumb: DocumentsDashboardBreadcrumb,
              }}
            >
              <IndexRoute
                component={DocumentsDashboard}
                props={{
                  breadcrumb: DocumentFoldersBreadcrumbs,
                }}
              />
              <Route
                path="new/"
                component={NewDocumentPage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
            </Route>
            <Route
              path="/procedures/"
              props={{
                activateNavLink: 'procedures',
                breadcrumb: ProceduresDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={ProceduresDashboard} />
              <Route
                path="new/"
                component={NewProcedurePage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
              <Route
                props={{
                  breadcrumb: MaybeProcedureProcessContainerBreadcrumb,
                }}
              >
                <Route
                  path=":hashid/(:slug)/"
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRoute component={ProcedureDetailPage} props={{}} />
                  <Route
                    path="edit/"
                    component={ForwardToProcedureEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route
                    path="generate_content/"
                    component={ForwardToProcedureEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route
                    path="version/:versionid"
                    props={{
                      breadcrumb: DocVersionBreadcrumb,
                    }}
                  >
                    <IndexRoute
                      component={ProcedureDetailPage}
                      props={{
                        showVersions: true,
                      }}
                    />
                    <Route
                      path="comment/"
                      component={ProcedureDetailPage}
                      props={{
                        showComments: true,
                      }}
                    />
                    <Route
                      path="accept/"
                      component={ProcedureDetailPage}
                      props={{
                        showAccept: true,
                      }}
                    />
                    <Route
                      path="edit/"
                      component={LoadableProcedureEditPage}
                      props={{
                        breadcrumb: EditDocBreadcrumb,
                      }}
                    />
                    <Route
                      path="generate_content/"
                      component={LoadableProcedureEditPage}
                      props={{
                        breadcrumb: EditDocBreadcrumb,
                      }}
                    />
                  </Route>
                  <Route path="tasks/">
                    <IndexRoute
                      component={ProcedureDetailPage}
                      props={{
                        showTasks: true,
                      }}
                    />
                    <Route path="report/">
                      <IndexRoute
                        component={ProcedureTaskReportPage}
                        props={{}}
                      />
                      <Route
                        path="(:reportid)/"
                        component={ProcedureTaskReportPage}
                        props={{}}
                      />
                    </Route>
                  </Route>
                  <Route
                    path="activity/"
                    component={ProcedureDetailPage}
                    props={{
                      showActivity: true,
                    }}
                  />
                  <Route
                    path="review/"
                    component={ProcedureDetailPage}
                    props={{
                      showReview: true,
                    }}
                  />
                  <Route
                    path="signoff/"
                    component={ProcedureDetailPage}
                    props={{
                      showSignoff: true,
                    }}
                  />
                  <Route
                    path="policies/"
                    component={ProcedureDetailPage}
                    props={{
                      showPolicies: true,
                    }}
                  />
                  <Route
                    path="processes/"
                    component={ProcedureDetailPage}
                    props={{
                      showProcesses: true,
                    }}
                  />
                  <Route
                    path="teams/"
                    component={ProcedureDetailPage}
                    props={{
                      showTeams: true,
                    }}
                  />
                </Route>
              </Route>
            </Route>

            <Route
              path="/tags/:tag"
              component={ProceduresDashboard}
              props={{
                activateNavLink: 'procedures',
                breadcrumb: ProceduresDashboardBreadcrumb,
              }}
            />

            <Redirect from="/folders/" to="documents/" />
            <Redirect from="/folders/:hashid/" to="documents/?folder=:hashid" />

            <Route
              path="/tags/"
              component={TagsDashboard}
              props={{
                activateNavLink: 'tags',
                breadcrumb: TagsDashboardBreadcrumb,
              }}
            />
            <Route
              path="/search/"
              props={{
                breadcrumb: SearchBreadcrumb,
              }}
              component={SearchDashboard}
            />

            <Route
              props={{
                activateNavLink: 'tasks',
                breadcrumb: TasksPageBreadcrumb,
              }}
            >
              <Route
                path="/procedures/:hashid/(:slug)/task/:taskid/"
                props={{
                  breadcrumb: DocNameBreadcrumb,
                }}
              >
                <IndexRoute component={TaskDetailPage} props={{}} />
                <Route
                  path="process/:processid/"
                  component={TaskDetailPage}
                  props={{
                    breadcrumb: InnerTaskDetailBreadcrumb,
                  }}
                />
                <Route
                  path="activity/"
                  component={TaskDetailPage}
                  props={{
                    showActivity: true,
                  }}
                />
                <Route
                  path="comments/"
                  component={TaskDetailPage}
                  props={{
                    showComments: true,
                  }}
                />
                <Route
                  path="tasks/"
                  component={TaskDetailPage}
                  props={{
                    showTasks: true,
                  }}
                />
              </Route>
            </Route>

            <Route
              path="/groups/"
              props={{
                activateNavLink: 'processes',
                breadcrumb: ProcessesDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={ProcessesDashboard} />
              <Route
                path="add/"
                component={ProcessCreatePage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
              <Route path=":hashid/(:slug)/">
                <Route
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRoute component={ProcessPage} props={{}} />
                  <Route
                    path="edit/"
                    component={ForwardToProcessEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route path="tasks/">
                    <IndexRoute
                      component={ProcessPage}
                      props={{
                        showTasks: true,
                      }}
                    />
                    <Route path="report/">
                      <IndexRoute
                        component={ProcessTaskReportPage}
                        props={{}}
                      />
                      <Route
                        path="(:reportid)/"
                        component={ProcessTaskReportPage}
                        props={{}}
                      />
                    </Route>
                  </Route>
                  <Route
                    path="policies/"
                    component={ProcessPage}
                    props={{
                      showPolicies: true,
                    }}
                  />
                  <Route
                    path="teams/"
                    component={ProcessPage}
                    props={{
                      showTeams: true,
                    }}
                  />
                  <Route
                    path="review/"
                    component={ProcessPage}
                    props={{
                      showReview: true,
                    }}
                  />
                  <Route
                    path="activity/"
                    component={ProcessPage}
                    props={{
                      showActivity: true,
                    }}
                  />
                  <Route
                    path="signoff/"
                    component={ProcessPage}
                    props={{
                      showSignoff: true,
                    }}
                  />
                  <Route
                    path="version/:versionid"
                    props={{
                      breadcrumb: DocVersionBreadcrumb,
                    }}
                  >
                    <IndexRoute
                      component={ProcessPage}
                      props={{
                        showVersions: true,
                      }}
                    />
                    <Route
                      path="accept/"
                      component={ProcessPage}
                      props={{
                        showAccept: true,
                      }}
                    />
                    <Route
                      path="comment/"
                      component={ProcessPage}
                      props={{
                        showComments: true,
                      }}
                    />
                    <Route
                      path="edit/"
                      component={LoadableProcessEditPage}
                      props={{
                        breadcrumb: EditDocBreadcrumb,
                      }}
                    />
                    <Route path="accept/" component={ProcessPage} />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route
              props={{
                activateNavLink: 'tasks',
                breadcrumb: TasksPageBreadcrumb,
              }}
            >
              <Route
                path="/groups/:hashid/(:slug)/task/:taskid/"
                props={{
                  breadcrumb: DocNameBreadcrumb,
                }}
              >
                <IndexRoute component={ProcessTaskPage} props={{}} />
                <Route
                  path="activity/"
                  component={ProcessTaskPage}
                  props={{
                    showActivity: true,
                  }}
                />
                <Route
                  path="comments/"
                  component={ProcessTaskPage}
                  props={{
                    showComments: true,
                  }}
                />
                <Route
                  path="tasks/"
                  component={ProcessTaskPage}
                  props={{
                    showTasks: true,
                  }}
                />
              </Route>
            </Route>

            <Route
              path="/policy"
              props={{
                activateNavLink: 'policies',
                breadcrumb: PoliciesDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={PoliciesDashboard} />
              <Route
                path="new/"
                component={NewPolicyPage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
              <Route
                path=":hashid/(:slug)/"
                props={{
                  breadcrumb: DocNameBreadcrumb,
                }}
              >
                <IndexRoute component={PolicyDetailPage} props={{}} />
                <Route
                  path="edit/"
                  component={ForwardToPolicyEditPage}
                  props={{
                    breadcrumb: EditDocBreadcrumb,
                  }}
                />
                <Route
                  path="activity/"
                  component={PolicyDetailPage}
                  props={{
                    showActivity: true,
                  }}
                />
                <Route
                  path="teams/"
                  component={PolicyDetailPage}
                  props={{
                    showTeams: true,
                  }}
                />
                <Route
                  path="procedures/"
                  component={PolicyDetailPage}
                  props={{
                    showProcedures: true,
                  }}
                />
                <Route
                  path="processes/"
                  component={PolicyDetailPage}
                  props={{
                    showProcesses: true,
                  }}
                />
                <Route
                  path="review/"
                  component={PolicyDetailPage}
                  props={{
                    showReview: true,
                  }}
                />
                <Route
                  path="signoff/"
                  component={PolicyDetailPage}
                  props={{
                    showSignoff: true,
                  }}
                />
                <Route
                  path="version/:versionid/"
                  props={{
                    breadcrumb: DocVersionBreadcrumb,
                  }}
                >
                  <IndexRoute
                    component={PolicyDetailPage}
                    props={{
                      showVersions: true,
                    }}
                  />
                  <Route
                    path="accept/"
                    component={PolicyDetailPage}
                    props={{
                      showAccept: true,
                    }}
                  />
                  <Route
                    path="comment/"
                    component={PolicyDetailPage}
                    props={{
                      showComments: true,
                    }}
                  />
                  <Route
                    path="edit/"
                    component={LoadablePolicyEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route path="accept/" component={PolicyDetailPage} />
                </Route>
              </Route>
            </Route>
            <Route
              path="/quiz"
              props={{
                activateNavLink: 'quizzes',
                breadcrumb: QuizzesDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={QuizzesDashboard} />
              <Route
                path="new/"
                component={NewQuizPage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
              <Route
                path="take/:hashid/(:slug)/"
                props={{breadcrumb: DocNameBreadcrumb}}
              >
                <IndexRoute component={RedirectToTakeQuizPage} />
                <Route path="take/:attemptid/">
                  <IndexRoute component={AttemptQuizPage} />
                  <Route path="result" component={AttemptResultPage} />
                </Route>
              </Route>

              <Route
                path=":hashid/(:slug)/"
                props={{
                  breadcrumb: DocNameBreadcrumb,
                }}
              >
                <IndexRoute component={QuizPage} props={{}} />
                <Route
                  path="edit/"
                  component={ForwardToQuizEditPage}
                  props={{
                    breadcrumb: EditDocBreadcrumb,
                  }}
                />
                <Route
                  path="teams/"
                  component={QuizPage}
                  props={{
                    showTeams: true,
                  }}
                />
                <Route
                  path="procedures/"
                  component={QuizPage}
                  props={{
                    showProcedures: true,
                  }}
                />
                <Route
                  path="policies/"
                  component={QuizPage}
                  props={{
                    showPolicies: true,
                  }}
                />
                <Route
                  path="processes/"
                  component={QuizPage}
                  props={{
                    showProcesses: true,
                  }}
                />
                <Route
                  path="attempts/"
                  component={QuizPage}
                  props={{
                    showAttempts: true,
                  }}
                />
                <Route
                  path="version/:versionid/"
                  props={{
                    breadcrumb: DocVersionBreadcrumb,
                  }}
                >
                  <IndexRoute
                    component={QuizPage}
                    props={{
                      showVersions: true,
                    }}
                  />
                  <Route
                    path="accept/"
                    component={QuizPage}
                    props={{
                      showAccept: true,
                    }}
                  />
                  <Route
                    path="edit/"
                    component={LoadableQuizEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route path="accept/" component={QuizPage} />
                </Route>
              </Route>
            </Route>

            <Route path="/people">
              <Route
                props={{
                  activateNavLink: 'teams',
                  breadcrumb: TeamsDashboardBreadcrumb,
                }}
              >
                <IndexRoute component={TeamsDashboard} />
                <Route
                  path="add-team/"
                  component={TeamCreatePage}
                  props={{
                    breadcrumb: CreateDocBreadcrumb,
                  }}
                />
                <Route
                  path="team/:teamid/:slug/"
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRoute component={TeamDetailPage} />
                  <Route path="users/" component={TeamDetailPage} />
                  <Route path="processes/" component={TeamDetailPage} />
                  <Route path="procedures/" component={TeamDetailPage} />
                  <Route path="policies/" component={TeamDetailPage} />
                  <Route path="folders/" component={TeamDetailPage} />
                  <Route path="signoffs/" component={TeamDetailPage} />
                  <Route path="products/" component={TeamDetailPage} />
                  <Route path="roles/" component={TeamDetailPage} />
                  <Route path="quizzes/" component={TeamDetailPage} />
                </Route>
              </Route>

              <Route
                path="users/"
                props={{
                  activateNavLink: 'members',
                  breadcrumb: UsersDashboardBreadcrumb,
                }}
              >
                <IndexRoute component={UsersDashboard} />
                <Route path="add">
                  <IndexRoute
                    component={UserCreatePage}
                    props={{
                      breadcrumb: UserCreateBreadcrumb,
                    }}
                  />
                </Route>
                <Route
                  path=":userid/"
                  component={UserDetailPage}
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRedirect to="invites/" />
                  <Route path="invites/" component={InvitationList} />
                  <Route path="teams/" component={TeamList} />
                  <Route path="activity/" component={ActivityList} />
                  <Route path="procedures/" component={ProcedureList} />
                  <Route path="processes/" component={ProcessList} />
                  <Route path="policies/" component={PoliciesList} />
                  <Route path="signoffs/" component={UserSignoffsList} />
                </Route>
              </Route>
            </Route>

            <Route path="/accounts/">
              <Route
                path="notifications"
                component={NotificationsPage}
                props={{
                  breadcrumb: NotificationsPageBreadcrumb,
                }}
              />
              <Route
                path="overview/*"
                props={{
                  breadcrumb: createStaticTextBreadcrumb('Account Overview'),
                }}
                component={LoadableAccountOverviewPage}
              />
              <Route
                path="switch"
                props={{
                  breadcrumb: createStaticTextBreadcrumb('Account Switch'),
                }}
                component={SwitchAccountPage}
              />
              <Route
                path="reports/"
                props={{
                  breadcrumb: ReportsBreadcrumb,
                }}
              >
                <IndexRoute component={ReportsPage} />
                <Route
                  path=":slug/"
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRoute component={ReportsDetailPage} />
                  <Route
                    path=":runid/"
                    component={ReportsRunPage}
                    props={{
                      breadcrumb: ReportsResultBreadcrumb,
                    }}
                  />
                </Route>
              </Route>
              <Route
                path="scim"
                props={{breadcrumb: SCIMBreadcrumb}}
                component={SCIMPage}
              />
              <Route
                path="referrer"
                props={{breadcrumb: createStaticTextBreadcrumb('Referrals')}}
                component={ReferralsPage}
              />
              <Route
                path="recycle"
                props={{breadcrumb: createStaticTextBreadcrumb('Recycle Bin')}}
                component={RecycleBinPage}
              />
              <Route
                path="kb/"
                props={{
                  activateNavLink: 'kbase',
                  breadcrumb: KBsDashboardBreadcrumb,
                }}
              >
                <IndexRoute component={KnowledgebasesDashboard} />
                <Route
                  path="new/"
                  component={NewKnowledgebasePage}
                  props={{
                    breadcrumb: CreateDocBreadcrumb,
                  }}
                />
                <Route
                  path="edit/:hashid/"
                  component={KBEditPage}
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                />
              </Route>
              <Route
                path="tokens/*"
                props={{
                  breadcrumb: createStaticTextBreadcrumb('API Settings'),
                }}
                component={APISettingsPage}
              />
              <Route
                path="webhooks/*"
                props={{
                  breadcrumb: createStaticTextBreadcrumb('Webhooks'),
                }}
                component={WebhookSettingsPage}
              />
              <Route component={WrapStaticContentPage}>
                <Route
                  path="settings/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('Account Overview'),
                  }}
                />
                <Route
                  path="b/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      'Add New Members in Bulk'
                    ),
                  }}
                />
                <Route
                  path="emailsettings/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      'Notification Settings'
                    ),
                  }}
                />
                <Route
                  path="two-factor-auth/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('Security'),
                  }}
                />
                <Route
                  path="password_change/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('Change Password'),
                  }}
                />
                <Route
                  path="billing/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('My Subscription'),
                  }}
                />
                <Route
                  path="stripe/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      'My Stripe Subscription'
                    ),
                  }}
                />
                <Route
                  path="migration/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('Migration Service'),
                  }}
                />
                <Route
                  path="recycle/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('Recycle'),
                  }}
                />
                <Route
                  path="export/pdf/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('Export'),
                  }}
                />
                <Route
                  path="createaccount/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('Create Account'),
                  }}
                />
                <Route
                  path="knowledgebase/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb('Knowledge Base'),
                  }}
                />
              </Route>
            </Route>

            <Route
              path="/help/*"
              component={WrapStaticContentPage}
              props={{
                breadcrumb: createStaticTextBreadcrumb('Help'),
              }}
            />

            <Route
              path="/tasks/"
              props={{
                activateNavLink: 'tasks',
                breadcrumb: TasksPageBreadcrumb,
              }}
            >
              <IndexRoute component={RedirecToLastUsedTasksDashboard} />
              <Route path="all/" component={TasksDashboard} />
              <Route path="mine/" component={TasksDashboard} />
              <Route
                path="do/:slug/:taskid/:timestamp(/:subnav)/"
                component={CreateTaskInstancePage}
              />
            </Route>
            <Route path="/*" component={HardRefreshToServer} />
          </Route>
        </Route>
      </Route>
    </Route>
  </Router>
);
